import {
  type Client,
  Client as ClientSchema,
  isClientContractComplete,
} from '@uit/shared/schemas/client';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { CreateClientDialog } from '../components/clients/CreateClientDialog.js';
import { DeleteClientAlert } from '../components/clients/DeleteClientAlert.js';
import { useApi } from '../lib/api.js';
import s from './ClientsListPage.module.scss';

const ClientList = z.array(ClientSchema);

export function ClientsListPage() {
  const { data, error, loading, refetch } = useApi<Client[]>('/api/clients', ClientList);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null);

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1 className={s.title}>Clients</h1>
          <p className={s.subtitle}>End clients of ultimumIT. Manage names + contract details.</p>
        </div>
        <button
          type="button"
          className={s.newButton}
          onClick={() => {
            setCreateOpen(true);
          }}
        >
          + New client
        </button>
      </div>

      {error && <div className={s.error}>Failed to load clients: {error.message}</div>}
      {!error && loading && !data && <div className={s.loading}>Loading clients…</div>}

      {data && data.length === 0 && (
        <div className={s.empty}>
          No clients yet. Click <strong>+ New client</strong> to add one.
        </div>
      )}

      {data && data.length > 0 && (
        <table className={s.table}>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Legal name</th>
              <th scope="col">Project / product</th>
              <th scope="col">Status</th>
              <th scope="col">Contract</th>
              <th scope="col" style={{ textAlign: 'right' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((client) => {
              const contractComplete = isClientContractComplete(client);
              return (
              <tr key={client.id}>
                <td>{client.name}</td>
                <td>{client.legal_name}</td>
                <td>{client.project_or_product_name}</td>
                <td>
                  <span className={client.active ? s.badgeActive : s.badgeInactive}>
                    {client.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <span className={contractComplete ? s.badgeActive : s.badgeInactive}>
                    {contractComplete ? 'Complete' : 'Incomplete'}
                  </span>
                </td>
                <td>
                  <div className={s.actions}>
                    <Link
                      to={`/clients/${client.id}`}
                      className={s.actionBtn}
                      aria-label={`Edit ${client.name}`}
                    >
                      Edit
                    </Link>
                    <button
                      type="button"
                      className={`${s.actionBtn} ${s.actionDelete}`}
                      onClick={() => {
                        setDeleteTarget(client);
                      }}
                      aria-label={`Delete ${client.name}`}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <CreateClientDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          void refetch();
        }}
      />

      {deleteTarget && (
        <DeleteClientAlert
          open={!!deleteTarget}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          client={deleteTarget}
          onDeleted={() => {
            void refetch();
          }}
        />
      )}
    </div>
  );
}
